import { DeepPick } from "../../../../types";
import { AiInterviewEvaluationFragment } from "../../../graphql";

export enum QuestionScoreLevel {
  EXCELLENT = 4,
  GOOD = 3,
  FAIR = 2,
  POOR = 1,
  UNSCORED = 0,
}

export const QUESTION_SCORE_COLORS = {
  [QuestionScoreLevel.EXCELLENT]: "green.600",
  [QuestionScoreLevel.GOOD]: "green.400",
  [QuestionScoreLevel.FAIR]: "yellow.400",
  [QuestionScoreLevel.POOR]: "red.400",
  [QuestionScoreLevel.UNSCORED]: "gray.300",
} as const;

export const QUESTION_SCORE_LABELS = {
  [QuestionScoreLevel.EXCELLENT]: "Excellent",
  [QuestionScoreLevel.GOOD]: "Good",
  [QuestionScoreLevel.FAIR]: "Fair",
  [QuestionScoreLevel.POOR]: "Poor",
  [QuestionScoreLevel.UNSCORED]: "Unscored",
} as const;

export const QUESTION_SCORE_LEVELS = [
  QuestionScoreLevel.EXCELLENT,
  QuestionScoreLevel.GOOD,
  QuestionScoreLevel.FAIR,
  QuestionScoreLevel.POOR,
] as const;

const scoreToScoreLevel = (score: number): QuestionScoreLevel => {
  if (score >= 3.5) return QuestionScoreLevel.EXCELLENT;
  if (score >= 3) return QuestionScoreLevel.GOOD;
  if (score >= 2) return QuestionScoreLevel.FAIR;
  if (score > 0) return QuestionScoreLevel.POOR;
  return QuestionScoreLevel.UNSCORED;
};

export const getQuestionScoreColor = (score: number): string => {
  const scoreLevel = scoreToScoreLevel(score);
  return QUESTION_SCORE_COLORS[scoreLevel];
};

export const getQuestionScoreLabel = (score: number): string => {
  const scoreLevel = scoreToScoreLevel(score);
  return QUESTION_SCORE_LABELS[scoreLevel];
};

/**
 * Returns question score counts formatted for display in the QuestionScoreCounts component.
 * Each item contains the color and count for a specific score level.
 */
export const getQuestionScoreCounts = (
  aiInterviewEvaluation: DeepPick<
    AiInterviewEvaluationFragment,
    "questions",
    "points"
  >
): Array<{
  color: string;
  count: number;
}> => {
  const questions = aiInterviewEvaluation.questions || [];

  const counts = {
    [QuestionScoreLevel.EXCELLENT]: 0,
    [QuestionScoreLevel.GOOD]: 0,
    [QuestionScoreLevel.FAIR]: 0,
    [QuestionScoreLevel.POOR]: 0,
  };

  questions.forEach((question) => {
    const scoreLevel = scoreToScoreLevel(question.points || 0);
    if (scoreLevel === QuestionScoreLevel.EXCELLENT) {
      counts[QuestionScoreLevel.EXCELLENT] += 1;
    } else if (scoreLevel === QuestionScoreLevel.GOOD) {
      counts[QuestionScoreLevel.GOOD] += 1;
    } else if (scoreLevel === QuestionScoreLevel.FAIR) {
      counts[QuestionScoreLevel.FAIR] += 1;
    } else if (scoreLevel === QuestionScoreLevel.POOR) {
      counts[QuestionScoreLevel.POOR] += 1;
    }
    // score 0 is not counted as it would be gray
  });

  return [
    {
      color: QUESTION_SCORE_COLORS[QuestionScoreLevel.EXCELLENT],
      count: counts[QuestionScoreLevel.EXCELLENT],
    },
    {
      color: QUESTION_SCORE_COLORS[QuestionScoreLevel.GOOD],
      count: counts[QuestionScoreLevel.GOOD],
    },
    {
      color: QUESTION_SCORE_COLORS[QuestionScoreLevel.FAIR],
      count: counts[QuestionScoreLevel.FAIR],
    },
    {
      color: QUESTION_SCORE_COLORS[QuestionScoreLevel.POOR],
      count: counts[QuestionScoreLevel.POOR],
    },
  ];
};

export const shouldShowScores = (
  aiInterviewEvaluation: Pick<
    AiInterviewEvaluationFragment,
    "points" | "maxPoints"
  >
): boolean => {
  return !!(
    aiInterviewEvaluation.points &&
    aiInterviewEvaluation.points > 0 &&
    aiInterviewEvaluation.maxPoints &&
    aiInterviewEvaluation.maxPoints > 0
  );
};

export const calculateFinalScore = (
  aiInterviewEvaluation: Pick<
    AiInterviewEvaluationFragment,
    "points" | "maxPoints"
  >
): number => {
  return Math.round(
    (100 * (aiInterviewEvaluation.points || 0)) /
      (aiInterviewEvaluation.maxPoints || 1)
  );
};

export const getScoreRange = (
  evaluation: Pick<AiInterviewEvaluationFragment, "points" | "maxPoints">
): string => {
  const points = evaluation.points || 0;
  const outOf = evaluation.maxPoints;
  if (outOf) {
    return `${points.toFixed(1)}/${outOf}`;
  }
  return `${points.toFixed(1)}`;
};

export const getFinalScoreColor = (score: number): string => {
  if (score > 75) {
    return "green.400";
  }
  if (score >= 50) {
    return "yellow.400";
  }
  return "red.400";
};
